import React, { useContext, useEffect, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import CardNE from '../../../../../shared/components/CardNE/CardNE';
import CardStatusNE from '../../../../../shared/components/CardStatusNE/CardStatusNE';
import DropzoneFileUploader from '../../../../../shared/components/DropzoneFileUploader/DropzoneFileUploader';
import StyledButtonNE from '../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import ToastNE from '../../../../../shared/components/ToastNE/ToastNE';
import { EXCEL_CONTENT_TYPE } from '../../../../../shared/constants/constants';
import { DOWNLOAD_IMAGES } from '../../../../../shared/constants/images';
import {
  ERROR_FILE_NAME,
  POINTS_FILE_VALIDATION_ERRORS,
  REWARDS_POINTS_UPLOADER_VALIDATORS
} from '../../../../../shared/constants/rewards';
import {
  CLICK_ACTION,
  DOWNLOAD_COLLABORATORS_POINTS_TEMPLATE,
  POINTS_DOWNLOAD_COLLABORATORS_FILE_ERRORS_AND_CONTINUE,
  POINTS_DOWNLOAD_COLLABORATORS_FILE_LATER
} from '../../../../../shared/constants/tagManager';
import { DOWNLOAD_TEMPLATE_ERROR } from '../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { IRewardsFileValidation } from '../../../../../shared/interfaces/rewards';
import Rewards from '../../../../../shared/services/api/rewards/rewards';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATION_POINTS_BY_FILE } from '../../../../../shared/translations/rewards';
import { base64ToFile, downloadFile } from '../../../../../shared/utils/utils';
import FileErrorModal from '../FileErrorModal/FileErrorModal';

interface UploadFileCardProps {
  setValidatedData: (pointsInfo: IRewardsFileValidation | undefined) => void;
  setFileInfo: (base64File: string | undefined) => void;
}

const UploadFileCard = ({ setValidatedData, setFileInfo }: UploadFileCardProps) => {
  const [downloadingTemplate, setDownloadingTemplate] = useState<boolean>(false);
  const [file, setFile] = useState<string>('');
  const [fileError, setFileError] = useState<string>();
  const [pointsInfo, setPointsInfo] = useState<IRewardsFileValidation>();
  const [loadingValidation, setLoadingValidation] = useState<boolean>(false);
  const [openPartialModal, setOpenPartialModal] = useState<boolean>(false);
  const [resetFile, setResetFile] = useState<boolean>(false);
  const [emptyValidation, setEmptyValidation] = useState<boolean>(false);
  const [showCorrectedFileMessage, setShowCorrectedFileMessage] = useState<boolean>(false);
  const [openErrorToast, setOpenErrorToast] = useState<boolean>(false);

  const { t } = useTranslation();
  const { companyData, company } = useContext(CompanyContext);

  const downloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      if (companyData.countryCode) {
        const response = await Rewards.getPointsTemplate(companyData.id, companyData.countryCode);
        downloadFile(response.url);
        TagManagerService.pushEvent(CLICK_ACTION, DOWNLOAD_COLLABORATORS_POINTS_TEMPLATE);
      }
    } catch {
      setOpenErrorToast(true);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const updateBase64 = (fileBase64: string) => {
    if (fileBase64) {
      setLoadingValidation(true);
      uploadFile(fileBase64);
      setFile(fileBase64);
      setFileError(undefined);
    } else {
      deleteFile();
    }
  };

  const deleteFile = () => {
    setFile('');
    setFileError(undefined);
    setValidatedData(undefined);
    setFileInfo(undefined);
    setShowCorrectedFileMessage(false);
    setEmptyValidation(false);
  };

  const uploadFile = async (fileBase64: string) => {
    try {
      const response = await Rewards.validatePointsFile(company.id, fileBase64);
      setPointsInfo(response);
      if (response.valid) {
        setValidatedData(response);
        setFileInfo(fileBase64);
      } else if (!response.validsCount && response.errors) {
        setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.noValidData));
        setEmptyValidation(true);
      } else {
        setOpenPartialModal(true);
      }
    } catch (error: any) {
      setShowCorrectedFileMessage(true);
      switch (error?.response?.data?.code) {
        case POINTS_FILE_VALIDATION_ERRORS.MAX_FILE_SIZE:
          setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.maxRows));
          break;
        case POINTS_FILE_VALIDATION_ERRORS.EMPTY_FILE_ERROR:
          setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.emptyFile));
          break;
        case POINTS_FILE_VALIDATION_ERRORS.INVALID_FORMAT:
          setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.format));
          break;
        default:
          setFileError(t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.error.default));
          break;
      }
    } finally {
      setLoadingValidation(false);
    }
  };

  const modalPrimaryButtonAction = () => {
    setValidatedData(pointsInfo);
    setFileInfo(file);
    setOpenPartialModal(false);
    downloadErrorFile();
    TagManagerService.pushEvent(CLICK_ACTION, POINTS_DOWNLOAD_COLLABORATORS_FILE_ERRORS_AND_CONTINUE);
  };

  const modalSecondaryButtonAction = () => {
    setResetFile(!resetFile);
    deleteFile();
    setOpenPartialModal(false);
    TagManagerService.pushEvent(CLICK_ACTION, POINTS_DOWNLOAD_COLLABORATORS_FILE_LATER);
  };

  const downloadErrorFile = () => {
    if (pointsInfo?.errors) {
      base64ToFile(pointsInfo?.errors, EXCEL_CONTENT_TYPE, ERROR_FILE_NAME);
    }
  };

  const handleCloseModal = (_: Record<string, unknown>, reason: string) => {
    if (reason && reason === 'backdropClick') {
      return true;
    }
  };

  const handleCloseToast = (event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason !== 'clickaway') setOpenErrorToast(false);
  };

  useEffect(() => {
    setResetFile(!resetFile);
    deleteFile();
  }, [company]);

  return (
    <CardNE className="justify-between gap-4" data-experience="new">
      <div className="flex flex-row w-full justify-between items-center">
        <h3 className="ne-font-body-lg font-bold m-0">{t(TRANSLATION_POINTS_BY_FILE.uploadFile.title)}</h3>
        <StyledButtonNE
          buttonType="secondary"
          buttonSize="sm"
          classes="w-64"
          imageLeft={DOWNLOAD_IMAGES}
          loading={downloadingTemplate}
          action={downloadTemplate}
        >
          {t(TRANSLATION_POINTS_BY_FILE.uploadFile.downloadTemplate)}
        </StyledButtonNE>
      </div>
      <div className="flex flex-row w-ful justify-between gap-4">
        <DropzoneFileUploader
          externalError={fileError}
          updateBase64={(base64File) => updateBase64(base64File)}
          reset={resetFile}
          loading={loadingValidation}
          validator={REWARDS_POINTS_UPLOADER_VALIDATORS}
          inputTitle={t(TRANSLATION_POINTS_BY_FILE.uploadFile.fileUploader.title)}
          correctedFileMessage={showCorrectedFileMessage}
          ActionButton={
            emptyValidation ? (
              <StyledButtonNE buttonType="secondary" action={downloadErrorFile} imageLeft={DOWNLOAD_IMAGES}>
                {t(TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsByFile.seeErrorsButton)}
              </StyledButtonNE>
            ) : undefined
          }
        />
        <div>
          <CardStatusNE
            type="informative"
            message={
              <Trans
                i18nKey={TRANSLATION_POINTS_BY_FILE.uploadFile.info.description}
                components={{
                  1: <b></b>,
                  2: <ul className="m-0 mt-8 ps-4"></ul>,
                  3: <li></li>
                }}
              />
            }
            className="h-full items-start justify-start"
          />
        </div>
      </div>
      <FileErrorModal
        isOpen={openPartialModal}
        validatedInfo={pointsInfo}
        primaryButtonAction={modalPrimaryButtonAction}
        secondaryButtonAction={modalSecondaryButtonAction}
        handleClose={handleCloseModal}
      />
      <ToastNE
        title={t(DOWNLOAD_TEMPLATE_ERROR.title)}
        description={t(DOWNLOAD_TEMPLATE_ERROR.description)}
        icon={DOWNLOAD_TEMPLATE_ERROR.icon}
        open={openErrorToast}
        button={DOWNLOAD_TEMPLATE_ERROR.button}
        buttonText={t(DOWNLOAD_TEMPLATE_ERROR.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </CardNE>
  );
};

export default UploadFileCard;
